import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  Injector,
  provideAppInitializer,
} from '@angular/core';
import { environment } from './environments/environment';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HammerModule, bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MsalService,
  MsalInterceptorConfiguration,
  ProtectedResourceScopes,
  MsalGuardConfiguration,
  MsalInterceptor,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
} from '@azure/msal-browser';
import Bugsnag from '@bugsnag/js';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular/dist/esm2015';
import {
  IfaaUiComponentsModule,
  UserServiceConfig,
} from '@ifaa-components/ui-components';
import { NgIdleModule } from '@ng-idle/core';
import { EffectsModule } from '@ngrx/effects';
import { MetaReducer, Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { RecaptchaV3Module, RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha-2';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import { NgrxFormsModule } from 'ngrx-forms';
import { ToastrModule } from 'ngx-toastr';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { MaterialModule } from './app/app.material.module';
import {
  msalElevatedConfig,
  msalAdviserElevatedConfig,
  msalConfig,
  protectedResources,
  loginRequest,
} from './app/auth-config';
import { FeatureToggleCheckService } from './app/helper/featureToggleHelper';
import { MessagePipe } from './app/modules/shared/pipes/message';
import { MemberPortalSharedModule } from './app/modules/shared/shared.module';
import { EmploymentService } from './app/modules/smrt/services/employment.service';
import { AccountDataGuardService } from './app/route-guard/account-data-guard.service';
import { AccountFeatureToggleGuardService } from './app/route-guard/account-feature-toggle-guard.service';
import { AccumGuardService } from './app/route-guard/accum-guard.service';
import { BlackoutGuardService } from './app/route-guard/blackout-guard.service';
import { ErrorPageGuardService } from './app/route-guard/error-page-guard.service';
import { FeatureFlagGuardService } from './app/route-guard/feature-flag-guard.service';
import { MemberFeatureToggleGuardService } from './app/route-guard/member-feature-toggle-guard.service';
import { MsalCheckService } from './app/route-guard/msal-check.service';
import { PensionGuardService } from './app/route-guard/pension-guard.service';
import { PermissionGuardService } from './app/route-guard/permission-guard.service';
import { ShowQuestionnaireGuardService } from './app/route-guard/show-questionnaire-guard.service';
import { AccessYourSuperService } from './app/services/access-your-super.service';
import { AccountsService } from './app/services/accounts.service';
import { BeneficiaryService } from './app/services/beneficiary.service';
import { BPAYDetailsService } from './app/services/bpay-details.service';
import { ChatService } from './app/services/chat.service';
import { CostOfCoverService } from './app/services/costofcover.service';
import { DocumentsService } from './app/services/documents.service';
import { FeatureToggleService } from './app/services/feature-toggle.service';
import { HelperService } from './app/services/helper.service';
import { HistoricalDataService } from './app/services/historical-data.service';
import { HttpCustomInterceptor } from './app/services/http-interceptor';
import { InsuranceQuestionnaireService } from './app/services/insurance-questionnaire.service';
import { InsuranceService } from './app/services/insurance.service';
import { InvestmentService } from './app/services/investment.service';
import { MemberCorrespondenceService } from './app/services/member-correspondence.service';
import { MemberNoteTypeService } from './app/services/member-note-type.service';
import { MemberPensionService } from './app/services/member-pension.service';
import { MembersService } from './app/services/members.service';
import { NoticeOfIntentTaxDeductionService } from './app/services/notice-of-intent-tax-deduction.service';
import { NotificationService } from './app/services/notification.service';
import { PersonalContributionService } from './app/services/personal-contrubution.service';
import { RolesService } from './app/services/roles.service';
import { RollInService } from './app/services/rollin.service';
import { StartAPensionService } from './app/services/start-a-pension.service';
import { SwitchInvestmentsService } from './app/services/switch-investments.service';
import { TakeMeWithYouService } from './app/services/take-me-with-you.service';
import { TransactionService } from './app/services/transaction.service';
import { UnderlyingInvestmentService } from './app/services/underlying-investment.service';
import { UnitPriceService } from './app/services/unit-price.service';
import { WebUserService } from './app/services/webuser.service';
import { reducers } from './app/store/app.reducers';
import { CommonEffects } from './app/store/common/common.effects';
import { clearState } from './app/store/meta-reducer';
import { GetTenantModule } from './app/tenant.helper';
import { TRANSLATE_MODULE_CONFIG } from './app/translateHttpLoader';
import { ChangePasswordEffects } from './app/views/change-password/effects';
import { CommonService } from './app/views/component-base';
import { TakeMeWithYouEffects } from './app/views/take-me-with-you-view/effects';
import { ResetSelectedAccountDataAction } from './app/modules/shared/components/member-account-dropdown/actions';

if (environment.production) {
  enableProdMode();
}

export const metaReducers: MetaReducer<any>[] = [clearState];
Bugsnag.start({
  apiKey: environment.bugsnagApiKey,
  releaseStage: environment.bugsnagEnv,
  enabledReleaseStages: ['Production', 'Staging', 'Uat', 'unknown'],
  onError: function (event) {
    event.addMetadata('tenant', {
      tenant: environment.code,
    });
  },
});

export let AppInjector: Injector;

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

export function errorHandlerFactory() {
  return new BugsnagErrorHandler();
}
let elevatedAccessPortalName = '';
function isElevatedAccess() {
  if (window.location.href.toLowerCase().indexOf('/fundelevated') >= 0) {
    elevatedAccessPortalName = 'FundPortal';
    return true;
  }

  if (window.location.href.toLowerCase().indexOf('/adviserelevated') >= 0) {
    elevatedAccessPortalName = 'AdviserPortal';
    return true;
  }

  return false;
}

export function MSALInstanceFactory(): IPublicClientApplication {
  if (isElevatedAccess()) {
    if (elevatedAccessPortalName == 'FundPortal')
      return new PublicClientApplication(msalElevatedConfig);

    if (elevatedAccessPortalName == 'AdviserPortal')
      return new PublicClientApplication(msalAdviserElevatedConfig);
  }

  return new PublicClientApplication(msalConfig);
}

export const initializeApp = async (msalService: MsalService, store: Store) => {
  await msalService.instance
    .initialize()
    .then(async () => {
      msalService.instance.enableAccountStorageEvents();
      // set active account when refreshing session
      const accounts = msalService.instance.getAllAccounts();

      // if there are accounts we can infer they are logging in via a stored token
      if (accounts.length > 0) {
        msalService.instance.setActiveAccount(accounts[0]);
      } else {
        store.dispatch(ResetSelectedAccountDataAction());
      }
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<
    string,
    Array<string | ProtectedResourceScopes> | null
  >();

  protectedResourceMap.set(
    protectedResources.apiEndpoint.endpoint.replace('api/**', 'assets/**'),
    null
  );
  protectedResourceMap.set(
    protectedResources.apiEndpoint.endpoint.replace(
      '**',
      'helper/systemconfig'
    ),
    null
  );
  protectedResourceMap.set(
    protectedResources.apiEndpoint.endpoint.replace(
      '**',
      'web-user/isMaintenanceMode'
    ),
    null
  );
  protectedResourceMap.set(protectedResources.apiEndpoint.endpoint, [
    {
      httpMethod: 'GET',
      scopes: [...protectedResources.apiEndpoint.scopes.read],
    },
    {
      httpMethod: 'POST',
      scopes: [...protectedResources.apiEndpoint.scopes.write],
    },
    {
      httpMethod: 'PUT',
      scopes: [...protectedResources.apiEndpoint.scopes.write],
    },
    {
      httpMethod: 'DELETE',
      scopes: [...protectedResources.apiEndpoint.scopes.write],
    },
  ]);

  return {
    interactionType: isElevatedAccess()
      ? InteractionType.Popup
      : InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: isElevatedAccess()
      ? InteractionType.Popup
      : InteractionType.Redirect,
    authRequest: loginRequest,
  };
}
const disableAnimations =
  !('animate' in document.documentElement) ||
  (navigator && /iPhone OS (8|9|10|11|12|13)_/.test(navigator.userAgent));

const myImports = [
  CommonModule,
  HammerModule,
  FormsModule,
  BrowserAnimationsModule.withConfig({ disableAnimations }),
  PdfJsViewerModule,
  RecaptchaV3Module,
  IfaaUiComponentsModule.forRoot({
    entryAppearance: 'outline',
    showEntryPlaceholder: false,
    googleApiKey: environment.googleApiKey,
    showButtonIcon: environment.showButtonIcon,
    apiUrl: environment.apiUrl,
    entryFileAttachedIcon: environment.entryFileAttachedIcon,
    entryFileDefaultIcon: environment.entryFileDefaultIcon,
    buttonTitleTextTransform: environment.buttonTitleTextTransform,
    titleTextTransform: environment.titleTextTransform,
    greenIdConfig: {
      environment: environment.greenIdEnv,
      code: environment.greenIdCode,
      iconVerified: environment.greenIdVerifiedIcon,
      iconNotVerified: environment.greenIdNotVerifiedIcon,
    },
    tenantCode: environment.code,
  } as UserServiceConfig),
  MemberPortalSharedModule,
  ToastrModule.forRoot({
    timeOut: 6000,
    positionClass: 'toast-bottom-right',
    preventDuplicates: true,
  }),
  MaterialModule,
  BrowserAnimationsModule,
  AppRoutingModule,
  TranslateModule.forRoot(TRANSLATE_MODULE_CONFIG),
  StoreModule.forRoot(reducers, { metaReducers }),
  StoreDevtoolsModule.instrument({
    maxAge: 20, // Retains last 25 states
    logOnly: environment.production, // Restrict extension to log-only mode
  }),
  ReactiveFormsModule,
  NgrxFormsModule,
  EffectsModule.forRoot([
    CommonEffects,
    ChangePasswordEffects,
    TakeMeWithYouEffects,
  ]),
  NgIdleModule.forRoot(),
];

GetTenantModule.getModule(myImports);

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(myImports),
    FeatureToggleCheckService,
    CurrencyPipe,
    DatePipe,
    MessagePipe,
    MembersService,
    EmploymentService,
    PensionGuardService,
    MsalCheckService,
    AccumGuardService,
    ShowQuestionnaireGuardService,
    FeatureFlagGuardService,
    AccountDataGuardService,
    AccountFeatureToggleGuardService,
    MemberFeatureToggleGuardService,
    BlackoutGuardService,
    ErrorPageGuardService,
    FeatureToggleService,
    AccountsService,
    HelperService,
    RolesService,
    PermissionGuardService,
    InsuranceService,
    BeneficiaryService,
    BPAYDetailsService,
    RollInService,
    TransactionService,
    TakeMeWithYouService,
    InvestmentService,
    MemberCorrespondenceService,
    UnderlyingInvestmentService,
    WebUserService,
    CostOfCoverService,
    MemberNoteTypeService,
    HistoricalDataService,
    SwitchInvestmentsService,
    InsuranceQuestionnaireService,
    MemberPensionService,
    UnitPriceService,
    DocumentsService,
    PersonalContributionService,
    NotificationService,
    CommonService,
    AccessYourSuperService,
    ChatService,
    NoticeOfIntentTaxDeductionService,
    StartAPensionService,
    [
      {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpCustomInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: MsalInterceptor,
        multi: true,
      },
    ],
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    provideAppInitializer(
      async () => await initializeApp(inject(MsalService), inject(Store))
    ),
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: ErrorHandler, useFactory: errorHandlerFactory },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptchaApi },
  ],
});
